// src/pages/Pincode.js
import React, { useState, useEffect } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import { MdEdit, MdDelete, MdVisibility } from 'react-icons/md';

import BASE_URL from '../../api';

const API_BASE_URL = `${BASE_URL}/api/master`;

const Pincode = () => {
  const [pincodes, setPincodes] = useState([]);
  const [states, setStates] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  };

  // ==================== FETCH DATA ====================
  const fetchPincodes = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/pincode`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch pincodes');
      const { data } = await res.json();
      setPincodes(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.message || 'Error');
    } finally {
      setLoading(false);
    }
  };

  const fetchStates = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/state`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch states');
      const { data } = await res.json();
      setStates(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchBranches = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/branch`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch branches');
      const { data } = await res.json();
      setBranches(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    const load = async () => {
      await Promise.all([fetchStates(), fetchBranches(), fetchPincodes()]);
    };
    if (localStorage.getItem('token')) load();
  }, []);

  // ==================== VALIDATION ====================
  const validate = (data) => {
    const errors = {};
    if (!data.pincode) errors.pincode = 'Pincode is required';
    else if (!/^\d{6}$/.test(data.pincode)) errors.pincode = 'Must be 6 digits';
    if (!data.locationname?.trim()) errors.locationname = 'Location is required';
    if (!data.stateid) errors.stateid = 'State is required';
    if (!data.branchid) errors.branchid = 'Branch is required';
    return errors;
  };

  // ==================== CRUD ====================
  const createPincode = async (data) => {
    const res = await fetch(`${API_BASE_URL}/pincode`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to create');
    return res.json();
  };

  const updatePincode = async (id, data) => {
    const res = await fetch(`${API_BASE_URL}/pincode/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to update');
    return res.json();
  };

  const deletePincode = async (id) => {
    const res = await fetch(`${API_BASE_URL}/pincode/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!res.ok) throw new Error('Failed to delete');
    return res.json();
  };

  // ==================== MODAL (Add/Edit) ====================
  const openModal = async (pincode = null) => {
    const isEdit = !!pincode;

    const { value: form } = await Swal.fire({
      title: isEdit ? 'Edit Pincode' : 'Add New Pincode',
      html: `
        <div class="space-y-6 text-left p-2">
          <div class="bg-gray-50 p-4 rounded-lg border border-gray-100">
            <h3 class="text-xs font-bold text-blue-600 uppercase mb-4 flex items-center gap-2">
              <span class="w-1.5 h-4 bg-blue-600 rounded-full"></span> Location Details
            </h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label class="block text-sm font-semibold text-gray-700 mb-1">Pincode *</label>
                <input id="pincode_val" class="swal2-input !m-0 !w-full !text-sm" value="${pincode?.pincode || ''}" maxlength="6">
              </div>
              <div>
                <label class="block text-sm font-semibold text-gray-700 mb-1">Location Name *</label>
                <input id="locationname" class="swal2-input !m-0 !w-full !text-sm" value="${pincode?.locationname || ''}">
              </div>
              <div>
                <label class="block text-sm font-semibold text-gray-700 mb-1">State *</label>
                <select id="stateid" class="swal2-select !m-0 !w-full !text-sm">
                  <option value="">Select State</option>
                  ${states
          .slice()
          .sort((a, b) => a.statename.localeCompare(b.statename))
          .map(s => `<option value="${s._id}" ${s._id === (pincode?.stateid?._id || pincode?.stateid) ? 'selected' : ''}>${s.statename}</option>`)
          .join('')}
                </select>
              </div>
              <div>
                <label class="block text-sm font-semibold text-gray-700 mb-1">Branch *</label>
                <select id="branchid" class="swal2-select !m-0 !w-full !text-sm">
                  <option value="">Select Branch</option>
                  ${branches
          .slice()
          .sort((a, b) => a.branchname.localeCompare(b.branchname))
          .map(b => `<option value="${b._id}" ${b._id === (pincode?.branchid?._id || pincode?.branchid) ? 'selected' : ''}>${b.branchcode} - ${b.branchname}</option>`)
          .join('')}
                </select>
              </div>
            </div>
          </div>

          <div class="bg-gray-50 p-4 rounded-lg border border-gray-100">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label class="block text-sm font-semibold text-gray-700 mb-1">Metro Type</label>
                <select id="ismetro" class="swal2-select !m-0 !w-full !text-sm">
                  <option value="Metro" ${pincode?.ismetro === 'Metro' ? 'selected' : ''}>Metro</option>
                  <option value="Non-Metro" ${pincode?.ismetro === 'Non-Metro' ? 'selected' : ''}>Non-Metro</option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-semibold text-gray-700 mb-1">Delivery Area (ODA)</label>
                <select id="isoda" class="swal2-select !m-0 !w-full !text-sm">
                  <option value="Non-ODA" ${pincode?.isoda === 'Non-ODA' ? 'selected' : ''}>Non-ODA</option>
                  <option value="ODA" ${pincode?.isoda === 'ODA' ? 'selected' : ''}>ODA</option>
                </select>
              </div>
            </div>
          </div>

          <div class="flex items-center ml-2">
            <input type="checkbox" id="status" class="w-4 h-4 text-blue-600 rounded" ${pincode?.status !== false ? 'checked' : ''}>
            <label for="status" class="ml-2 text-sm font-medium text-gray-700">Active Status</label>
          </div>
        </div>
      `,
      width: '800px',
      showCancelButton: true,
      confirmButtonText: isEdit ? 'Update' : 'Save',
      preConfirm: () => {
        const data = {
          pincode: document.getElementById('pincode_val').value.trim(),
          locationname: document.getElementById('locationname').value.trim(),
          stateid: document.getElementById('stateid').value,
          branchid: document.getElementById('branchid').value,
          ismetro: document.getElementById('ismetro').value,
          isoda: document.getElementById('isoda').value,
          status: document.getElementById('status').checked,
        };
        const errors = validate(data);
        if (Object.keys(errors).length > 0) {
          Swal.showValidationMessage(Object.values(errors).join('<br>'));
          return false;
        }
        return data;
      },
    });

    if (form) {
      try {
        setLoading(true);
        isEdit ? await updatePincode(pincode._id, form) : await createPincode(form);
        toast.success('Pincode saved!');
        fetchPincodes();
      } catch (err) {
        toast.error(err.message || 'Error');
      } finally {
        setLoading(false);
      }
    }
  };

  // ==================== FILTER & PAGINATION ====================
  const filtered = pincodes.filter(p =>
    p.pincode.includes(filter) ||
    p.locationname?.toLowerCase().includes(filter.toLowerCase()) ||
    states.find(s => s._id === p.stateid?._id)?.statename?.toLowerCase().includes(filter.toLowerCase())
  );

  const totalPages = Math.ceil(filtered.length / pageSize);
  const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <ToastContainer />
      <h2 className="text-2xl font-bold mb-6">Pincode Master</h2>
      <div className="bg-white rounded-xl shadow p-6">
        <div className="flex justify-between gap-4 mb-6">
          <input
            type="text" placeholder="Search pincode or location..."
            value={filter} onChange={e => { setFilter(e.target.value); setCurrentPage(1); }}
            className="px-4 py-2 border rounded-md w-80"
          />
          <button onClick={() => openModal()} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
            Add Pincode
          </button>
        </div>

        {loading ? (
          <div className="text-center py-8">Loading...</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full table-auto border-collapse">
                <thead className="bg-blue-600 text-white">
                  <tr>
                    <th className="p-3 text-left">S.No</th>
                    <th className="p-3 text-left">Pincode</th>
                    <th className="p-3 text-left">Location</th>
                    <th className="p-3 text-left">State</th>
                    <th className="p-3 text-left">Metro</th>
                    <th className="p-3 text-left">Status</th>
                    <th className="p-3 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map((p, i) => {
                    const state = states.find(s => s._id === p.stateid?._id);
                    // Search Index Fix
                    const sNo = filter
                      ? pincodes.findIndex(original => original._id === p._id) + 1
                      : (currentPage - 1) * pageSize + i + 1;

                    return (
                      <tr key={p._id} className="border-b hover:bg-gray-50">
                        <td className="p-3 text-gray-600">{sNo}</td>
                        <td className="p-3 font-medium">{p.pincode}</td>
                        <td className="p-3">{p.locationname}</td>
                        <td className="p-3">{state?.statename || '-'}</td>
                        <td className="p-3">{p.ismetro}</td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded text-xs ${p.status ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {p.status ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="p-3 text-center">
                          <div className="flex justify-center gap-3">
                            <button onClick={() => openModal(p)} className="text-blue-600"><MdEdit size={18} /></button>
                            <button onClick={async () => {
                              if (window.confirm('Delete?')) {
                                await deletePincode(p._id);
                                fetchPincodes();
                              }
                            }} className="text-red-600"><MdDelete size={18} /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex justify-between items-center mt-6">
                <span className="text-sm">Showing {paginated.length} of {filtered.length} entries</span>
                <div className="flex gap-2">
                  <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50">Prev</button>
                  <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50">Next</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Pincode;